import { useMemo } from "react";

import Button from "components/Button";
import Icons from "components/Icons";

import { sampleCode } from "components/hosted-scraping/apiPlaygroundTemplates";

import { ApiPlaygroundLocationState } from "routes/dashboard/ApiPlaygroundEditDetails";
import { useUser } from "routes/dataroutes/UserData";

import { QuickStartStep, useQuickStartContext } from "./QuickStartCard";
import { QuickStartCodeView } from "./QuickStartCodeView";
import { trackWelcomeDashboardGoToApiPlayground } from "utils/Tracking";
import OutputFormatSelect from "./OutputFormatSelect";

export default function MarkdownQuickStartSteps() {
  const user = useUser();

  const { programmingLanguage, selectedOutputFormat } = useQuickStartContext();

  const codeViewContext: Omit<ApiPlaygroundLocationState, "language"> = useMemo(() => {
    return {
      collectorType: "async_urls",
      scrapingMethod: "api",
      language: 'python',
      url: "https://nytimes.com/",
      outputFormat: selectedOutputFormat ?? codeViewContext.outputFormat ?? 'markdown',
    };
  }, [selectedOutputFormat]);

  const codeExample = useMemo(() => {
    return sampleCode(
      programmingLanguage,
      codeViewContext.scrapingMethod,
      codeViewContext.collectorType,
      codeViewContext.url,
      user?.apiKey || "",
      {outputFormat: codeViewContext.outputFormat}
    );
  }, [programmingLanguage, codeViewContext.scrapingMethod, codeViewContext.collectorType, codeViewContext.url, codeViewContext.outputFormat, user?.apiKey]);

  return (
    <>
      <QuickStartStep
        title="Select your preferred output format"
        index={ 2 }
      >
        <OutputFormatSelect/>
      </QuickStartStep>
      <QuickStartStep
        title="Test run your first request"
        subtitle="Copy the snippet, change the URL to scrape the page you want, and send your first request."
        index={ 3 }
      >
        <QuickStartCodeView language={ programmingLanguage } codeExample={ codeExample } canZoom />
      </QuickStartStep>
      <QuickStartStep
        title="Test run your request in our API Playground"
        subtitle="Learn more about all available parameters and the costs for your request, while scraping your URL in the API Playground."
        icon={ <Icons.APIPlayground className="h-4" /> }
      >
        <Button
          text="Go to API Playground"
          href={`/apiplayground?method=${codeViewContext.scrapingMethod}&input=${encodeURIComponent(codeViewContext.url)}&outputFormat=${codeViewContext.outputFormat}`} 
          size="SM"
          className="button button-accent"
          onClick={() => trackWelcomeDashboardGoToApiPlayground('clicked')}
        />
      </QuickStartStep>
    </>
  );
};
